import { Radio, MenuItem, CircularProgress } from '@mui/material';
import { US_STATES } from 'constants/usStates';
import CANADA_PROVINCES from 'constants/canadaProvince';
import { RHFSelect, RHFTextField } from 'components/RHF';
import { StoreFormPropsType } from 'features/store/types';
import { CustomButton } from 'components/Button';

const StoreForm = ({
    methods,
    plan,
    loading,
    products,
    loadingProducts,
    billingPlans,
    selectedBillingPlanKey,
    onBillingPlanChange,
}: StoreFormPropsType) => {
    const selectedBillingPlan = billingPlans.find((billingPlan) => billingPlan.key === selectedBillingPlanKey) || billingPlans[0];
    const scannerProduct = products.find((product) => product.name === "Lottoreco Preconfigured Scanner");
    const scannerPrice = typeof scannerProduct?.price === 'number' ? scannerProduct.price : 0;
    const totalPrice = plan === "Preconfigured Scanner"
        ? selectedBillingPlan.price + scannerPrice
        : selectedBillingPlan.price;
    const totalPriceLabel = `$${totalPrice.toFixed(2)}`;

    const handleStateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const selectedValue = e.target.value;

        const isCanadaProvince = CANADA_PROVINCES.some(province => province.value === selectedValue);

        const isUSState = US_STATES.some(state => state.code === selectedValue);

        if (isCanadaProvince) {
            methods.setValue('country', 'CA');
            methods.setValue('state', selectedValue);
            setTimeout(() => methods.trigger(['country', 'state']), 0);
        } else if (isUSState) {
            methods.setValue('country', 'US');
            methods.setValue('state', selectedValue);
            setTimeout(() => methods.trigger(['country', 'state']), 0);
        } else {
            console.log("No valid selection or empty value");
        }
    }

    const handleCountryChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const selectedValue = e.target.value;
        methods.setValue('country', selectedValue);
        methods.setValue('state', '');
        setTimeout(() => methods.trigger(['country', 'state']), 0);
    }

    return (<>
        <div className="store-form-container">
            {/* Store Information Section */}
            <div className="form-section mb-32">
                <div className="form-section-header mb-16">
                    <p className="form-section-title m-0">Store Information</p>
                </div>
                <div className="divider-8-24" />
                
                <div className="form-fields">
                    <div className="form-field mb-20">
                        <p className="field-label mb-6 d-block">Store Name<span className="required-asterisk">*</span></p>
                        <RHFTextField name="name" placeholder="Enter the full legal name of your store" />
                    </div>
                    
                    <div className="form-field mb-20">
                        <p className="field-label mb-6 d-block">Address<span className="required-asterisk">*</span></p>
                        <RHFTextField name="address_line1" placeholder="Enter Address" />
                    </div>

                    <div className="form-row d-flex gap-20">
                        <div className="form-field flex-1">
                            <p className="field-label mb-6 d-block">Country<span className="required-asterisk">*</span></p>
                            <RHFSelect name="country" placeholder="Select Country" onChange={handleCountryChange}>
                                <MenuItem value="" disabled>Select Country</MenuItem>
                                <MenuItem value={"US"}>United States</MenuItem>
                                <MenuItem value={"CA"}>Canada</MenuItem>
                            </RHFSelect>
                        </div>

                        <div className="form-field flex-1">
                            <p className="field-label mb-6 d-block">State/Province<span className="required-asterisk">*</span></p>
                            <RHFSelect name="state" placeholder="Select State" onChange={handleStateChange}>
                                {methods.watch("country") === "CA" && (CANADA_PROVINCES.map((province) => (
                                    <MenuItem key={province.value} value={province.value}>
                                        {province.province}
                                    </MenuItem>
                                ))
                                )}
                                {methods.watch("country") === "US" && (US_STATES.map((state) => (
                                    <MenuItem key={state.code} value={state.code}>
                                        {state.name}
                                    </MenuItem>
                                ))
                                )}
                            </RHFSelect>
                        </div>
                    </div>

                    <div className="form-row d-flex gap-20">
                        <div className="form-field flex-1">
                            <p className="field-label mb-6 d-block">City<span className="required-asterisk">*</span></p>
                            <RHFTextField name="city" placeholder="Enter City" />
                        </div>

                        <div className="form-field flex-1">
                            <p className="field-label mb-6 d-block">Zip/Postal Code<span className="required-asterisk">*</span></p>
                            <RHFTextField name="postal_code" placeholder="Enter Zip/Postal Code" />
                        </div>
                    </div>
                </div>
            </div>

            {/* Subscription Plan Section */}
            <div className="form-section mb-32">
                <div className="form-section-header mb-16">
                    <p className="form-section-title m-0">Subscription Plan</p>
                </div>
                <div className="divider-8-24" />

                <div className="subscription-plan">
                    <div className="billing-plan-options" role="radiogroup" aria-label="Billing plan">
                        {billingPlans.map((billingPlan) => {
                            const isSelected = billingPlan.key === selectedBillingPlanKey;
                            const inputId = `billing-plan-${billingPlan.key}`;

                            return (
                                <label
                                    key={billingPlan.key}
                                    htmlFor={inputId}
                                    className={`billing-plan-option d-flex items-center justify-between p-16 ${isSelected ? 'selected' : ''}`}
                                >
                                    <div className="plan-info d-flex items-center gap-12">
                                        <Radio
                                            id={inputId}
                                            checked={isSelected}
                                            value={billingPlan.key}
                                            name="billing_plan"
                                            onChange={() => onBillingPlanChange(billingPlan.key)}
                                            inputProps={{ 'aria-label': billingPlan.label }}
                                        />
                                        <div className="plan-text">
                                            <div className="plan-name-row d-flex items-center gap-8 mb-4">
                                                <p className="plan-name m-0">{billingPlan.label}</p>
                                                {billingPlan.badge && (
                                                    <span className="plan-badge">{billingPlan.badge}</span>
                                                )}
                                            </div>
                                            <p className="plan-description m-0">{billingPlan.description}</p>
                                        </div>
                                    </div>
                                    <div className="plan-price d-flex flex-column items-end">
                                        <p className="m-0">{billingPlan.priceLabel}</p>
                                        <span>{billingPlan.cadenceLabel}</span>
                                    </div>
                                </label>
                            );
                        })}
                    </div>
                    {loadingProducts && (
                        <div className="product-loading-note d-flex items-center gap-8 mt-12">
                            <CircularProgress size={16} />
                            <span>Loading add-ons</span>
                        </div>
                    )}
                </div>
            </div>

            <div className="form-footer mt-32 pt-20">
                <div className="total-section d-flex items-center gap-8 mb-20">
                    <p className="total-label">Total:</p>
                    <div className="total-price-group">
                        <p className="total-price">{totalPriceLabel}</p>
                        <span>{selectedBillingPlan.cadenceLabel}</span>
                    </div>
                </div>
            </div>
            <CustomButton
                className="button-base button-compact submit-button w-full px-24 py-12"
                type="submit"
                loading={loading}
            >Continue</CustomButton>
        </div>
    </>
    );
};

export default StoreForm; 
